import AlertMgr from '@app/custom-api/alert-custom-api'

// Constants
import {GENERIC_MESSAGES, API_ERROR_MESSAGE} from '@app/constants'

// Contexts
import {IPContext} from '@app/custom-contexts'

// Hooks
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useBespokeContactFields} from '@app/components/forms/useBespokeContactFields'
import {useContext, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSendEmail} from '@app/hooks/use-send-email'
import {useToast} from '@app/hooks/use-toast'
import useMultiSite from './use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'

// Utils
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {validateRecaptcha} from '@app/utils/recaptchaUtils'
import {findDialCodeByCountry} from '@app/utils/utils'

const FORM_PREFIX = 'bespoke_'

/*
How to use?
(...)
import ReCAPTCHA from 'react-google-recaptcha';
const MyComponent = () => {
    const config = getConfig()
    const [siteKey, setSiteKey] = useState(config.app.RECAPTCHA_SITE_KEY);
    const [captchaValue, setCaptchaValue] = useState(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    console.log("Captcha value:", value);
  };

  return (
    <div>
      <form>
        <ReCAPTCHA
          sitekey={siteKey}
          onChange={handleCaptchaChange}
        />
        <button type="submit" disabled={!captchaValue}>
          Submit
        </button>
      </form>
    </div>
  );
}
*/

/**
 * Hook for handling bespoke contact form submission
 * @param {object} form - React Hook Form instance
 */
export const useBespokeContact = (form) => {
    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const {ip} = useContext(IPContext)
    const {sendEmail} = useSendEmail()
    const {site, locale} = useMultiSite()

    const formFields = useBespokeContactFields({form, prefix: FORM_PREFIX})
    const intl = useIntl()
    const recaptchaRef = useRef(null)
    const toast = useToast()
    const {triggerBespokeFormEvent} = useGTM()

    const alertMgr = new AlertMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    /**
     * Handle form submission
     * reCaptcha is checked before submitting
     * @param {object} values - Form field values
     * @returns Promise<void>
     */
    const onSubmit = async (values) => {
        const captchaValue = await recaptchaRef.current.executeAsync()
        const reCaptchaRes = await validateRecaptcha(captchaValue, ip)

        if (!reCaptchaRes?.success) {
            toast({
                title: intl.formatMessage(GENERIC_MESSAGES.CAPTCHA_FAILED),
                description: '',
                status: 'error'
            })
            return
        }

        // Construct the phone number only if the phone country is provided
        const phoneCountry = values[FORM_PREFIX + 'phoneCountry']
        const phoneNumber = values[formFields.phone.name]
        const userPhone = phoneNumber
            ? `${findDialCodeByCountry(phoneCountry)} ${phoneNumber}`.trim()
            : ''

        const emailPayload = {
            emailType: 'bespokeEmail',
            firstName: values[formFields.firstName.name],
            lastName: values[formFields.lastName.name],
            emailID: values[formFields.email.name],
            phone: userPhone,
            project: values[formFields.project.name],
            company: values[formFields.companyName.name],
            comments: values[formFields.comments.name]
        }

        const {success, message} = await sendEmail(emailPayload)

        if (!success) {
            const errorMessage =
                message ||
                intl.formatMessage({
                    id: 'bespoke_contact.form.submit.failure.title',
                    defaultMessage: 'There was an error submitting the bespoke contact form.'
                })
            toast({
                title: intl.formatMessage(API_ERROR_MESSAGE),
                description: errorMessage,
                status: 'error'
            })
            const accessToken = await getTokenWhenReady()
            await alertMgr.sendAlert(
                {
                    context: {
                        siteId: site.id,
                        message: errorMessage,
                        initialRequest: emailPayload,
                        category: 'Bespoke Form',
                        order: '',
                        page: '',
                        job: '',
                        asset: '',
                        identifier: '',
                        fileName: '',
                        lineNumber: ''
                    },
                    alertType: 'business',
                    logging: 'no'
                },
                accessToken
            )

            return
        }

        triggerBespokeFormEvent()

        toast({
            title: intl.formatMessage({
                id: 'bespoke_contact.form.submit.success.title',
                defaultMessage: 'Thank you for contacting Vilebrequin.'
            }),
            description: intl.formatMessage({
                id: 'bespoke_contact.form.submit.success.description',
                defaultMessage: 'We will get back to you as soon as possible.'
            }),
            status: 'success'
        })
        form.reset()
    }

    return {
        onSubmit,
        formFields,
        recaptchaRef
    }
}
